import React from "react";

import { PlaceholderImage } from "@components/atoms";
import { useNetworkStatus } from "@hooks";
import NoPhoto from "images/no-photo.svg";

import { IImage } from "@types";

export const CachedImage: React.FC<IImage> = ({
  url,
  url2x,
  alt,
  children,
  defaultImage = NoPhoto,
  ...props
}: IImage) => {
  const [fallbackForUrl, setFallbackForUrl] = React.useState<
    string | undefined
  >();
  const [unavailableUrl, setUnavailableUrl] = React.useState<
    string | undefined
  >();
  const { online } = useNetworkStatus();

  const useFallback = Boolean(
    url && fallbackForUrl === url && defaultImage && defaultImage !== url
  );
  const activeUrl = useFallback ? defaultImage : url;
  const isUnavailable = Boolean(activeUrl && unavailableUrl === activeUrl);

  React.useEffect(() => {
    updateAvailability();
  }, [online, activeUrl]);

  async function updateAvailability() {
    let _isUnavailable = false;
    if ("caches" in window) {
      if (!online) {
        const match = await window.caches.match(activeUrl!);
        let match2x;
        if (url2x && activeUrl === url) {
          match2x = await window.caches.match(url2x);
        }
        if (!match && !match2x) {
          _isUnavailable = true;
        }
      }
    }

    if (_isUnavailable) {
      setUnavailableUrl(activeUrl);
    } else if (unavailableUrl === activeUrl) {
      setUnavailableUrl(undefined);
    }
  }

  if (!activeUrl || isUnavailable) {
    return children || <PlaceholderImage alt={alt} />;
  }

  const srcSet =
    activeUrl === url && url2x
      ? `${activeUrl} 1x, ${url2x} 2x`
      : `${activeUrl} 1x`;

  return (
    <img
      {...props}
      src={activeUrl}
      srcSet={srcSet}
      alt={alt}
      // navigator.onLine is not always accurate
      onError={() => {
        if (!children && !useFallback && defaultImage && url) {
          setFallbackForUrl(url);
          return;
        }
        setUnavailableUrl(activeUrl);
      }}
    />
  );
};
